import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { IRosterResponse } from '$models'
import { exceptionMessageHandler } from '$utils'
import { rosterService } from '$services'
import { ShiftType } from '$constants'

interface RostersState {
  activeRosterTemplates: IRosterResponse[]
  deactivatedRosterTemplates: IRosterResponse[]
}

export interface RostersSlice extends RostersState {
  /**
   * Resets Rosters Slice state
   */
  resetRostersSlice: () => void

  /**
   * Get Roster Templates list
   * @param includeDeleted Denotes whether to include deleted templates
   * @param shiftTypesToInclude Optional array of ShiftType to filter templates' shifts by
   */
  getRosterTemplates: (
    includeDeleted?: boolean,
    shiftTypesToInclude?: ShiftType[]
  ) => Promise<void>
}

const initialState: RostersState = {
  activeRosterTemplates: [],
  deactivatedRosterTemplates: [],
}

const createRostersSlice: StateCreator<
  RostersSlice & SharedSlice,
  [],
  [],
  RostersSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetRostersSlice: () => set(initialState),

  getRosterTemplates: async (
    includeDeleted: boolean = false,
    shiftTypesToInclude: ShiftType[] = []
  ): Promise<void> => {
    set(() => ({ isLoading: true }))
    return rosterService
      .getTemplates(includeDeleted, shiftTypesToInclude)
      .then((result: IRosterResponse[]) => {
        if (!includeDeleted) {
          return set(() => ({
            activeRosterTemplates: result,
          }))
        } else {
          const deactivatedTemplates = result.filter(
            (template) => template.datestampDeleted != null
          )
          return set(() => ({
            deactivatedRosterTemplates: deactivatedTemplates,
          }))
        }
      })
      .catch((error) => {
        if (!includeDeleted) {
          set(() => ({
            activeRosterTemplates: [],
          }))
        } else {
          set(() => ({
            deactivatedRosterTemplates: [],
          }))
        }
        exceptionMessageHandler(error, 'Failed to retrieve roster templates')
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },
})

export default createRostersSlice
